import { createWithEqualityFn } from 'zustand/traditional'
import axios from 'axios'
import { cleanParamsFilter } from 'Utils/cleanParamsFilter'
import { downloadBlob } from 'Utils/downloadBlob'
import type { ScanInventoryType } from 'Utils/scanInventoryType'

import { CRUDBaseStore } from './Base/CRUD'

interface ProjectStore extends CRUDBaseStore {
  totalData: number
  setTotalData: (totalData: number) => void
  piles: any[]
  pileHoles: any[]
  pileHole: any
  weldPictures: any[]
  machines: any[]
  availableSiteSupervisors: any[]
  availableSaleUsers: any[]
  weeklyReport: {
    planStatus?: string
    planStatusColor?: string
    data: any[]
  }
  getOne: (id: number) => Promise<{ success: boolean; data: any }>
  getAllPagination: (filter: any) => Promise<{ success: boolean; data: any }>
  getPiles: (id: number) => Promise<{ success: boolean; data: any }>
  returnPile: (
    projectId: number,
    inventoryId: number
  ) => Promise<{ success: boolean; data: any }>
  getAvailableSiteSupervisors: () => Promise<{ success: boolean; data: any }>
  getAvailableSaleUsers: () => Promise<{ success: boolean; data: any }>
  getWeeklyReport: (
    projectId: number,
    from: string,
    to: string
  ) => Promise<{ success: boolean; data: any }>
  setPiles: (data: any) => void
  setPileHoles: (data: any) => void
  setPileHole: (data: any) => void
  scanReceivePile: (
    id: number,
    data: { inventoryCode: string; type?: ScanInventoryType; itemId?: string }
  ) => Promise<{ success: boolean; data: any }>
  scanReceivePileTest: (
    data: { inventoryCode: string; type?: ScanInventoryType; soNo?: string; itemId?: string }
  ) => Promise<{ success: boolean; data: any }>
  scanReceivePileFromShippingRequestId: (
    id: number,
    shippingRequestId: number
  ) => Promise<{ success: boolean; data: any }>
  pileDriving: (
    projectId: number,
    data: {
      inventoryCode: string
      type?: ScanInventoryType
      itemId?: string
      pileHoleId?: number | undefined | null
      projectMachineId?: number | undefined | null
      pileNo?: string | null
      gridLine?: string | null
      location?: string | null
    }
  ) => Promise<{ success: boolean; data: any }>
  getPileHoles: (projectId: number) => Promise<{ success: boolean; data: any }>
  getPileHoleInfo: (
    projectId: number,
    pileHoldeId: number
  ) => Promise<{ success: boolean; data: any }>
  createPileHole: (
    projectId: number,
    data: { executionMode?: 'drive' | 'prebore_only'; projectMachineId?: number }
  ) => Promise<{ success: boolean; data: any }>
  executePileHolePrebore: (
    projectId: number,
    pileHoleId: number,
    data: {
      preboreDepth?: number | null
      preboreDrillSize?: number | null
      projectMachineId?: number
      pileNo?: string | null
      gridLine?: string | null
      location?: string | null
    }
  ) => Promise<{ success: boolean; data: any }>
  updatePileHolePrebore: (
    projectId: number,
    pileHoleId: number,
    data: {
      preboreDepth: number | null
      preboreDrillSize: number | null
      pileNo?: string | null
      gridLine?: string | null
      location?: string | null
    }
  ) => Promise<{ success: boolean; data: any }>
  removePileDriving: (
    projectId: number,
    data: { inventoryCode: string; pileHoleId: number; type?: ScanInventoryType; itemId?: string }
  ) => void
  open: (projectId: number) => Promise<{ success: boolean; data: any }>
  close: (projectId: number) => Promise<{ success: boolean; data: any }>
  closeHole: (
    projectId: number,
    data: { pileHoleId: number }
  ) => Promise<{ success: boolean; data: any }>
  changePileHoleStatus: (
    projectId: string,
    pileHoleId: string,
    status: string
  ) => Promise<{ success: boolean; data: any }>
  // Welds Picture
  getAllWeldPictures: (
    projectId: string,
    pileHoleId: string
  ) => Promise<{ success: boolean; data: any }>
  viewWeldPicture: (path: string) => string
  createWeldPicture: (
    projectId: string,
    pileHoleId: string,
    data: any
  ) => Promise<{ success: boolean; data: any }>
  deleteWeldPicture: (
    projectId: string,
    pileHoleId: string,
    id: string
  ) => Promise<{ success: boolean; data: any }>
  // Welds Picture
  downloadAllDailyReport: () => Promise<{ success: boolean; data: any }>
  downloadDailyReportByProjetId: (projectId: string) => Promise<{ success: boolean; data: any }>
  exportDailyReportByDate: (reportDate: string) => Promise<{ success: boolean; data: any }>
  getAllProjectMachine: (projectId: string) => Promise<{ success: boolean; data: any }>
  assignMachineToProject: (projectId: string, data: any) => Promise<{ success: boolean; data: any }>
  updateMachineToProject: (
    projectId: string,
    machineId: string,
    data: any
  ) => Promise<{ success: boolean; data: any }>
  deleteMachineToProject: (
    projectId: string,
    itemId: string
  ) => Promise<{ success: boolean; data: any }>
  assignSiteSupervisor: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  unassignSiteSupervisor: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  assignSaleUser: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  unassignSaleUser: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  addNote: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  removeNote: (projectId: number, noteId: number) => Promise<{ success: boolean; data: any }>
  downloadFileNote: (
    projectId: number,
    noteId: number,
    fileId: string,
    documentName: string
  ) => Promise<{ success: boolean; data: any }>
  getJobClosingList: (projectId: number) => Promise<{ success: boolean; data: any }>
  getJobClosing: (projectId: number, id: number | string) => Promise<{ success: boolean; data: any }>
  createJobClosing: (projectId: number, body: any) => Promise<{ success: boolean; data: any }>
  updateJobClosing: (
    projectId: number,
    id: number | string,
    body: any
  ) => Promise<{ success: boolean; data: any }>
  issueJobClosing: (projectId: number, id: number | string) => Promise<{ success: boolean; data: any }>
  uploadJobClosingSignedFile: (
    projectId: number,
    id: number | string,
    file: File,
    signedByName: string
  ) => Promise<{ success: boolean; data: any }>
  getJobClosingSignedFile: (
    projectId: number,
    id: number | string
  ) => Promise<{ success: boolean; data: any }>
  downloadJobClosingPdf: (
    projectId: number,
    id: number | string,
    fileName: string
  ) => Promise<{ success: boolean; data?: any }>
  clearState: () => void
}

export const API_SLUG = 'project'

export const INITIAL_STATE = {}

export const useProjectStore = createWithEqualityFn<ProjectStore>((set, get) => ({
  data: [],
  piles: [],
  pileHoles: [],
  availableSiteSupervisors: [],
  availableSaleUsers: [],
  pileHole: null,
  weldPictures: [],
  machines: [],
  selected: INITIAL_STATE,
  weeklyReport: {
    planStatus: undefined,
    planStatusColor: undefined,
    data: [],
  },
  loading: false,
  error: undefined,
  totalData: 0,
  setTotalData: (totalData: number) => {
    set({ totalData })
  },
  setLoading: (loading: boolean) => {
    set({ loading })
  },
  setError: (error: any) => {
    set({ error })
  },
  setSelected: (selected: any) => {
    set({ selected })
  },
  setPiles: (data: any) => {
    set({ piles: data })
  },
  setPileHoles: (data: any) => {
    set({ pileHoles: data })
  },
  setPileHole: (data: any) => {
    set({ pileHole: data })
  },
  getAll: async (filter: any) => {
    set({ loading: true })
    const finaleFilter = cleanParamsFilter(filter)

    return axios
      .get(`/${API_SLUG}`, { params: finaleFilter })
      .then(res => {
        set({ data: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAllPagination: async (filter: any) => {
    set({ loading: true })
    const finaleFilter = cleanParamsFilter(filter)

    return axios
      .get(`/${API_SLUG}/pagination`, { params: finaleFilter })
      .then(res => {
        const { totalCount, data } = res.data
        set({ data, totalData: totalCount })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getOne: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ selected: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAvailableSiteSupervisors: async () => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/available-site-supervisors`)
      .then(res => {
        set({ availableSiteSupervisors: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAvailableSaleUsers: async () => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/sales`)
      .then(res => {
        set({ availableSaleUsers: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getWeeklyReport: async (projectId, from, to) => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${projectId}/weekly-report`, {
        params: { projectId, from, to },
      })
      .then(res => {
        const payload = res.data
        const weeklyReport = Array.isArray(payload)
          ? { planStatus: undefined, planStatusColor: undefined, data: payload }
          : {
              planStatus: payload?.planStatus,
              planStatusColor: payload?.planStatusColor,
              data: Array.isArray(payload?.data) ? payload.data : [],
            }

        set({ weeklyReport })
        set({ loading: false })

        return { success: true, data: weeklyReport }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getPiles: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}/piles`)
      .then(res => {
        set({ piles: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  returnPile: async (projectId, inventoryId) => {
    const formData = new FormData()
    formData.append('inventoryId', inventoryId.toString())

    return axios
      .post(`/${API_SLUG}/${projectId}/return-pile`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getPileHoles: async projectId => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${projectId}/pile-hole`)
      .then(res => {
        set({ pileHoles: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ pileHoles: [] })
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString(), err }
      })
  },
  getPileHoleInfo: async (projectId: number, pileHoleId: number) => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${projectId}/pile-hole/${pileHoleId}`)
      .then(res => {
        set({ pileHole: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  createPileHole: async (projectId: number, data) => {
    set({ loading: true })

    return axios
      .post(`/${API_SLUG}/${projectId}/pile-hole`, {
        executionMode: data.executionMode,
        projectMachineId: data.projectMachineId,
      })
      .then(res => {
        const created = res.data
        const state = get()
        set({
          loading: false,
          pileHoles: [...state.pileHoles, created],
          pileHole: created,
        })
        return { success: true, data: created }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  executePileHolePrebore: async (projectId: number, pileHoleId: number, data) => {
    set({ loading: true })

    return axios
      .post(`/${API_SLUG}/${projectId}/pile-hole/${pileHoleId}/prebore/execute`, {
        preboreDepth: data.preboreDepth ?? null,
        preboreDrillSize: data.preboreDrillSize ?? null,
        projectMachineId: data.projectMachineId,
        pileNo: data.pileNo ?? null,
        gridLine: data.gridLine ?? null,
        location: data.location ?? null,
      })
      .then(res => {
        const updated = res.data
        const state = get()
        const pileHoles = state.pileHoles.map((hole: any) =>
          hole.id === pileHoleId ? updated : hole
        )
        set({
          loading: false,
          pileHoles,
          pileHole: updated,
        })
        return { success: true, data: updated }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  updatePileHolePrebore: async (projectId, pileHoleId, data) => {
    set({ loading: true })

    return axios
      .patch(`/${API_SLUG}/${projectId}/pile-hole/${pileHoleId}/prebore`, {
        preboreDepth: data.preboreDepth,
        preboreDrillSize: data.preboreDrillSize,
        pileNo: data.pileNo ?? null,
        gridLine: data.gridLine ?? null,
        location: data.location ?? null,
      })
      .then(res => {
        const updated = res.data
        const state = get()
        const pileHoles = state.pileHoles.map((hole: any) =>
          hole.id === pileHoleId ? updated : hole
        )

        set({
          loading: false,
          pileHoles,
          pileHole: state.pileHole?.id === pileHoleId ? updated : state.pileHole,
        })

        return { success: true, data: updated }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  create: async (data: any) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  edit: async (id, data) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .put(`/${API_SLUG}/${id}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  delete: async id => {
    set({ loading: true })
    return axios
      .delete(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  open: async id => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/open`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  close: async id => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/close`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  scanReceivePile: async (id, data) => {
    set({ loading: true })
    const formData = new FormData()
    formData.append('inventoryCode', data.inventoryCode)
    formData.append('Type', data.type ?? 'd365')
    if (data.itemId) formData.append('itemId', data.itemId)

    return axios
      .post(`/${API_SLUG}/${id}/scan-receive`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  scanReceivePileTest: async data => {
    set({ loading: true })
    const formData = new FormData()
    formData.append('inventoryCode', data.inventoryCode)
    formData.append('type', data.type ?? 'd365')
    if (data.soNo) {
      formData.append('soNo', data.soNo)
    }
    if (data.itemId) {
      formData.append('itemId', data.itemId)
    }

    return axios
      .post(`/${API_SLUG}/scan-receive/test`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data ?? err?.response?.data?.message?.toString() }
      })
  },
  scanReceivePileFromShippingRequestId: async (id, shippingRequestId) => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/scan-receive/shipping-request/${shippingRequestId}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  pileDriving: async (
    projectId: number,
    data: {
      inventoryCode: string
      type?: ScanInventoryType
      itemId?: string
      pileHoleId?: number | undefined | null
      projectMachineId?: number | undefined | null
      pileNo?: string | null
      gridLine?: string | null
      location?: string | null
    }
  ) => {
    set({ loading: true })
    const formData = new FormData()

    formData.append('inventoryCode', data.inventoryCode)
    formData.append('Type', data.type ?? 'd365')
    if (data.itemId) formData.append('itemId', data.itemId)

    if (data.pileHoleId) formData.append('pileHoleId', data.pileHoleId.toString())
    if (data.projectMachineId) formData.append('projectMachineId', data.projectMachineId.toString())
    if (data.pileNo) formData.append('pileNo', data.pileNo)
    if (data.gridLine) formData.append('gridLine', data.gridLine)
    if (data.location) formData.append('location', data.location)

    return axios
      .post(`/${API_SLUG}/${projectId}/pile-driving`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  removePileDriving: async (
    projectId: number,
    data: { inventoryCode: string; pileHoleId: number; type?: ScanInventoryType; itemId?: string }
  ) => {
    set({ loading: true })
    const formData = new FormData()

    formData.append('inventoryCode', data.inventoryCode)
    formData.append('pileHoleId', data.pileHoleId.toString())
    formData.append('Type', data.type ?? 'd365')
    if (data.itemId) formData.append('itemId', data.itemId)

    return axios
      .delete(`/${API_SLUG}/${projectId}/pile-driving`, { data: formData })
      .then(res => {
        set({ loading: false })
        return true
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return false
      })
  },
  closeHole: async (projectId: number, data: { pileHoleId: number }) => {
    set({ loading: true })
    const formData = new FormData()
    formData.append('pileHoleId', data.pileHoleId.toString())

    return axios
      .post(`/${API_SLUG}/${projectId}/close-hole`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  changePileHoleStatus: async (projectId: string, pileHoleId: string, status: string) => {
    const formData = new FormData()
    formData.append('status', status)

    return axios
      .patch(`/${API_SLUG}/${projectId}/pile-hole/${pileHoleId}/status`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAllWeldPictures: async (projectId: string, pileHoleId: string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/piles/${pileHoleId}/attachWeldsPicture`)
      .then(res => {
        set({ weldPictures: res.data })
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  viewWeldPicture: (path: string) => {
    return `${process.env.REACT_APP_BACKEND_API_URL}${path}`
  },
  createWeldPicture: async (projectId: string, pileHoleId: string, data: any) => {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}/${projectId}/piles/${pileHoleId}/attachWeldsPicture`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  deleteWeldPicture: async (projectId: string, pileHoleId: string, id: string) => {
    return axios
      .delete(`/${API_SLUG}/${projectId}/piles/${pileHoleId}/attachWeldsPicture/${id}`)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadAllDailyReport: async () => {
    return axios
      .get(`/${API_SLUG}/dailyReport`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'daily-report.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadDailyReportByProjetId: async (projectId: string) => {
    return axios
      .get(`/${API_SLUG}/dailyReport?projectId=${projectId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'daily-report.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  exportDailyReportByDate: async (reportDate: string) => {
    return axios
      .get(`/${API_SLUG}/dailyReport/export`, {
        params: { reportDate },
        responseType: 'blob',
      })
      .then(res => {
        const contentDisposition = res.headers['content-disposition']
        const fileNameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        const fileName =
          fileNameMatch?.[1]?.replace(/['"]/g, '') ??
          `DailyReport_${reportDate.replace(/\//g, '')}.xlsx`

        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        return { success: true, data: res.data }
      })
      .catch(async err => {
        const blobData = err?.response?.data
        if (blobData instanceof Blob) {
          const text = await blobData.text()
          try {
            const json = JSON.parse(text)
            return { success: false, data: json.message }
          } catch {
            return { success: false, data: text }
          }
        }
        return {
          success: false,
          data: err?.response?.data?.message?.toString() ?? 'เกิดข้อผิดพลาด',
        }
      })
  },
  getAllProjectMachine: async (projectId: string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/machines`)
      .then(res => {
        set({ machines: res.data })
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  assignMachineToProject: async (projectId: string, data: any) => {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}/${projectId}/machines`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  updateMachineToProject: async (projectId: string, machineId: string, data: any) => {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key])
      }
    })

    return axios
      .patch(`/${API_SLUG}/${projectId}/machines/${machineId}`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  deleteMachineToProject: async (projectId: string, machineId: string) => {
    return axios
      .delete(`/${API_SLUG}/${projectId}/machines/${machineId}`)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  assignSiteSupervisor: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/assign-site-supervisor`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  unassignSiteSupervisor: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/remove-site-supervisor`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  assignSaleUser: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/assign-sale-user`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  unassignSaleUser: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/remove-sale-user`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  addNote: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/add-note`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  removeNote: async (projectId: number, noteId: number) => {
    return axios
      .delete(`/${API_SLUG}/${projectId}/delete-note/${noteId}`)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadFileNote: async (
    projectId: number,
    noteId: number,
    fileId: string,
    documentName: string
  ) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/notes/${noteId}/file/${fileId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', documentName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getJobClosingList: async (projectId: number) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/job-closing`)
      .then(res => ({ success: true, data: res.data }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  getJobClosing: async (projectId: number, id: number | string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/job-closing/${id}`)
      .then(res => ({ success: true, data: res.data }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  createJobClosing: async (projectId: number, body: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/job-closing`, body)
      .then(res => ({ success: true, data: res.data }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  updateJobClosing: async (projectId: number, id: number | string, body: any) => {
    return axios
      .patch(`/${API_SLUG}/${projectId}/job-closing/${id}`, body)
      .then(res => ({ success: true, data: res.data }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  issueJobClosing: async (projectId: number, id: number | string) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/job-closing/${id}/issue`)
      .then(res => ({ success: true, data: res.data }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  uploadJobClosingSignedFile: async (
    projectId: number,
    id: number | string,
    file: File,
    signedByName: string
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('signedByName', signedByName.trim())
    return axios
      .post(`/${API_SLUG}/${projectId}/job-closing/${id}/signed-file`, formData)
      .then(res => ({ success: true, data: res.data }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  getJobClosingSignedFile: async (projectId: number, id: number | string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/job-closing/${id}/signed-file`, {
        responseType: 'blob',
      })
      .then(res => ({
        success: true,
        data: URL.createObjectURL(res.data as Blob),
      }))
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  downloadJobClosingPdf: async (projectId: number, id: number | string, fileName: string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/job-closing/${id}/pdf`, {
        responseType: 'blob',
      })
      .then(res => {
        downloadBlob(new Blob([res.data], { type: 'application/pdf' }), fileName)
        return { success: true }
      })
      .catch(err => ({
        success: false,
        data: err?.response?.data?.message?.toString(),
      }))
  },
  clearState: () => {
    set({
      data: [],
      piles: [],
      pileHoles: [],
      weldPictures: [],
      pileHole: null,
      totalData: 0,
      loading: false,
      error: undefined,
      selected: INITIAL_STATE,
    })
  },
}))
